import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useForwarding } from '../../contexts/ForwardingContext'
import { toast } from '../shared/NotificationToast'
import { getCopilotSessions, getErrorMessage, type CopilotSession } from '../../utils/api'
import type { ReturnTo } from '../../contexts/forwarding'
import { FeatureRequestModal } from './FeatureRequestModal'

/** The chat/ticket a bubble belongs to — supplies the round-trip `returnTo`. */
export interface ForwardSource {
  kind: 'chat' | 'ticket'
  id: string | number
  label: string
  path: string
}

const CopyIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75"
    />
  </svg>
)

const CheckIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
)

const SparkleIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
    />
  </svg>
)

interface Props {
  /** Plain text to copy / forward. */
  text: string
  /** Source chat/ticket; when absent (e.g. WhatsApp views) only Copy shows. */
  source?: ForwardSource
  /** Visual alignment so the row hugs the correct side of the bubble. */
  align?: 'start' | 'end'
  /** Present only on YOUR OWN sent messages → shows "Edit". */
  onEdit?: () => void
  /** Present only when the message has an edit history → shows "View versions". */
  onViewVersions?: () => void
  /** The message's id — enables the "Feature request" action (needs `source` too). */
  messageId?: string | number
}

const PencilIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
    />
  </svg>
)

const HistoryIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const BulbIcon: React.FC = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
  </svg>
)

interface MenuPos {
  left: number
  top: number | null
  bottom: number | null
  maxHeight: number
}

const MENU_W = 256 // matches w-64

/**
 * Hover action row attached to a chat/ticket message bubble: Copy plain text,
 * and (when a source is known) Forward into the AI Copilot. Forwarding opens a
 * small chooser of the agent's existing Copilot sessions plus "New chat", then
 * navigates to /ai-copilot where the composer is prefilled + focused (never
 * auto-sent).
 *
 * The chooser is rendered in a PORTAL with fixed positioning so it can never be
 * clipped by the chat thread's scroll container (which previously hid the top of
 * the menu, including "New chat"). "New chat" is pinned at the top; only the
 * session list below it scrolls.
 */
export const MessageForwardActions: React.FC<Props> = ({ text, source, align = 'start', onEdit, onViewVersions, messageId }) => {
  const [featureRequestOpen, setFeatureRequestOpen] = useState(false)
  const navigate = useNavigate()
  const { forwardToCopilot } = useForwarding()
  const [copied, setCopied] = useState(false)
  const [chooserOpen, setChooserOpen] = useState(false)
  const [sessions, setSessions] = useState<CopilotSession[] | null>(null)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [pos, setPos] = useState<MenuPos | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const forwardBtnRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const trimmed = (text || '').trim()

  const copy = async () => {
    let ok = false
    // Preferred path (secure contexts / HTTPS).
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(trimmed)
        ok = true
      }
    } catch {
      /* fall through to the legacy fallback */
    }
    // Fallback for non-secure contexts or older browsers.
    if (!ok) {
      try {
        const ta = document.createElement('textarea')
        ta.value = trimmed
        ta.style.position = 'fixed'
        ta.style.left = '-9999px'
        ta.setAttribute('readonly', '')
        document.body.appendChild(ta)
        ta.select()
        ok = document.execCommand('copy')
        document.body.removeChild(ta)
      } catch {
        ok = false
      }
    }
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  // Position the portal menu relative to the Forward button, flipping above the
  // button when there isn't room below, and clamping to the viewport.
  const computePos = () => {
    const btn = forwardBtnRef.current
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const MARGIN = 8
    const spaceBelow = window.innerHeight - r.bottom - MARGIN
    const spaceAbove = r.top - MARGIN
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow
    let left = align === 'end' ? r.right - MENU_W : r.left
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - MENU_W - MARGIN))
    if (openUp) {
      setPos({ left, top: null, bottom: window.innerHeight - r.top + 6, maxHeight: Math.min(spaceAbove, 380) })
    } else {
      setPos({ left, top: r.bottom + 6, bottom: null, maxHeight: Math.min(spaceBelow, 380) })
    }
  }

  const openChooser = async () => {
    if (chooserOpen) {
      setChooserOpen(false)
      return
    }
    computePos()
    setChooserOpen(true)
    if (sessions === null && !loadingSessions) {
      setLoadingSessions(true)
      try {
        setSessions(await getCopilotSessions())
      } catch (err) {
        toast.error('Could not load chats', getErrorMessage(err, 'Please try again'))
        setSessions([])
      } finally {
        setLoadingSessions(false)
      }
    }
  }

  const doForward = (sessionId: string | number | 'new') => {
    const returnTo: ReturnTo | null = source
      ? { kind: source.kind, id: source.id, label: source.label, path: source.path }
      : null
    forwardToCopilot({ text: trimmed, sessionId, returnTo })
    setChooserOpen(false)
    navigate('/ai-copilot')
  }

  // Close the chooser on outside click / Escape; close (rather than drift) on
  // scroll or resize so the fixed menu never detaches from its button.
  useEffect(() => {
    if (!chooserOpen) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (wrapRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setChooserOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setChooserOpen(false)
    }
    const onResize = () => setChooserOpen(false)
    const onScroll = (e: Event) => {
      // Scrolling INSIDE the menu's own session list must NOT close it — only
      // close when the page / chat thread scrolls (which would otherwise detach
      // the fixed-position menu from its button).
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return
      setChooserOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true) // capture: catch the thread's scroller
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [chooserOpen])

  if (!trimmed) return null

  return (
    <div
      ref={wrapRef}
      className={`mt-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity ${
        align === 'end' ? 'justify-end' : 'justify-start'
      }`}
    >
      <button
        type="button"
        onClick={copy}
        title="Copy message"
        className="inline-flex items-center gap-1 text-[11px] text-txt-tertiary hover:text-brand-600 transition-colors rounded px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? 'Copied' : 'Copy'}
      </button>

      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          title="Edit this message"
          className="inline-flex items-center gap-1 text-[11px] text-txt-tertiary hover:text-brand-600 transition-colors rounded px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <PencilIcon />
          Edit
        </button>
      )}

      {onViewVersions && (
        <button
          type="button"
          onClick={onViewVersions}
          title="View previous versions of this message"
          className="inline-flex items-center gap-1 text-[11px] text-txt-tertiary hover:text-brand-600 transition-colors rounded px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <HistoryIcon />
          View versions
        </button>
      )}

      {source && (
        <button
          type="button"
          onClick={() => setFeatureRequestOpen(true)}
          title="Turn this message into a feature request"
          className="inline-flex items-center gap-1 text-[11px] text-txt-tertiary hover:text-brand-600 transition-colors rounded px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <BulbIcon />
          Feature request
        </button>
      )}

      {featureRequestOpen && source && (
        <FeatureRequestModal
          originalText={text}
          sourceKind={source.kind}
          sourceId={source.id}
          messageId={messageId}
          onClose={() => setFeatureRequestOpen(false)}
        />
      )}

      {source && (
        <button
          ref={forwardBtnRef}
          type="button"
          onClick={openChooser}
          aria-haspopup="menu"
          aria-expanded={chooserOpen}
          title="Forward to AI Copilot"
          className="inline-flex items-center gap-1 text-[11px] text-txt-tertiary hover:text-brand-600 transition-colors rounded px-1 py-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <SparkleIcon />
          Forward to Copilot
        </button>
      )}

      {chooserOpen &&
        source &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[60] w-64 rounded-lg border border-border bg-surface-elevated shadow-float flex flex-col overflow-hidden animate-slide-up"
            style={{
              left: pos.left,
              top: pos.top ?? undefined,
              bottom: pos.bottom ?? undefined,
              maxHeight: pos.maxHeight,
            }}
          >
            {/* Frozen header + New chat (never scrolls) */}
            <div className="flex-none p-1 border-b border-border bg-surface-elevated">
              <p className="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">
                Forward to AI Copilot
              </p>
              <button
                type="button"
                role="menuitem"
                onClick={() => doForward('new')}
                className="w-full text-left px-2.5 py-2 rounded-md text-sm font-medium text-brand-700 dark:text-brand-300 hover:bg-surface-hover transition-colors flex items-center gap-2"
              >
                <SparkleIcon />
                New chat
              </button>
            </div>

            {/* Scrollable existing-session list */}
            <div className="flex-1 overflow-y-auto p-1">
              {loadingSessions ? (
                <p className="px-2.5 py-2 text-xs text-txt-tertiary">Loading chats…</p>
              ) : sessions && sessions.length > 0 ? (
                sessions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="menuitem"
                    onClick={() => doForward(s.id)}
                    className="w-full text-left px-2.5 py-2 rounded-md text-sm text-txt-primary hover:bg-surface-hover transition-colors truncate"
                    title={s.title}
                  >
                    {s.title || 'Untitled chat'}
                  </button>
                ))
              ) : (
                <p className="px-2.5 py-2 text-xs text-txt-tertiary">No existing chats</p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
